import { Link, useLocation } from "@tanstack/react-router";
import { type Api } from "api-client";
import { Icon } from "icons";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  SidebarMenuButton,
} from "ui";

import { useAuthContext } from "src/auth";

import { AccountMenuOrganizationItem } from "./AccountMenuOrganizationItem";
import { AccountMenuPersonalWorkspaceItem } from "./AccountMenuPersonalWorkspaceItem";
import { CurrentUserAvatar } from "./CurrentUserAvatar";
import { OrganizationAvatar } from "./OrganizationAvatar";

type Organization = Api.ListOrganizations.Output.Organization;

interface AccountMenuContentProps {
  canCreateOrganizations: boolean;
  organizations: Organization[];
  sidebar?: boolean;
}

function isOrganizationPath(pathname: string, slug: string): boolean {
  const organizationPath = `/orgs/${slug}`;
  return (
    pathname === organizationPath || pathname.startsWith(`${organizationPath}/`)
  );
}

export const AccountMenuContent = observer(function AccountMenuContent(
  props: AccountMenuContentProps,
) {
  const authContext = useAuthContext();
  const { t } = useTranslation();
  const user = authContext.store.user;
  const showOrganizations =
    props.organizations.length > 0 || props.canCreateOrganizations;
  const pathname = useLocation({
    select: (location) => {
      return location.pathname;
    },
  });
  const activeOrganization = props.organizations.find((organization) => {
    return isOrganizationPath(pathname, organization.slug);
  });
  const identityName = activeOrganization?.name ?? user.name;
  const avatar = activeOrganization ? (
    <OrganizationAvatar
      name={activeOrganization.name}
      src={activeOrganization.avatarUrl ?? undefined}
    />
  ) : (
    <CurrentUserAvatar />
  );
  const trigger = props.sidebar ? (
    <SidebarMenuButton size="lg" tooltip={identityName}>
      {avatar}
      <div className="flex min-w-0 flex-col text-left group-data-[collapsible=icon]:hidden">
        <span className="truncate font-medium">{identityName}</span>
        <span className="truncate text-xs text-muted-foreground">
          {user.email}
        </span>
      </div>
    </SidebarMenuButton>
  ) : (
    <button type="button" aria-label={identityName}>
      {avatar}
    </button>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        className="min-w-64"
        side={props.sidebar ? "right" : "bottom"}
        align="start"
      >
        <DropdownMenuLabel className="flex items-center gap-4">
          {avatar}
          <div className="flex min-w-0 flex-col">
            <span className="truncate">{identityName}</span>
            <span className="truncate text-xs font-normal">{user.email}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {authContext.store.isCoach && showOrganizations ? (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              {t("organizations.title")}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="min-w-56">
              {props.organizations.length > 0 &&
              authContext.store.hasCoachSubscriptionAccess ? (
                <>
                  <AccountMenuPersonalWorkspaceItem />
                  <DropdownMenuSeparator />
                </>
              ) : null}
              {props.organizations.length === 0 ? (
                <DropdownMenuItem disabled>
                  {t("organizations.empty")}
                </DropdownMenuItem>
              ) : (
                props.organizations.map((organization) => {
                  return (
                    <AccountMenuOrganizationItem
                      key={organization.id}
                      organization={organization}
                    />
                  );
                })
              )}
              {props.canCreateOrganizations ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/create-org">
                      <Icon name="tabler-plus" />
                      {t("organizations.create")}
                    </Link>
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ) : null}
        <DropdownMenuItem asChild>
          {activeOrganization ? (
            <Link
              className="capitalize"
              to="/orgs/$slug/settings"
              params={{ slug: activeOrganization.slug }}
            >
              {t("settings")}
            </Link>
          ) : (
            <Link className="capitalize" to="/settings">
              {t("settings")}
            </Link>
          )}
        </DropdownMenuItem>
        <form action="/logout" method="post">
          <DropdownMenuItem asChild>
            <button className="w-full cursor-pointer" type="submit">
              {t("logOut")}
            </button>
          </DropdownMenuItem>
        </form>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
